import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import PayeeFooterBar from '../PayeeFooterBar';
import PickerLayout from '../../pickerLayout/components/LayoutContainer/LayoutContainer';
import { hasApiCallInProgress } from '../../states/api/selectors';
import { NameNotCheckedModal } from '../Notifications/NameNotCheckedModal';
import { get } from 'lodash';
import { getPayeeById } from '../../states/commonSelectors/selectors';
import { getPayeeSrdCheck } from '../../states/payees/actions';
import { isIFT } from '../../states/modes/selectors';

const mapState = (state) => ({
	hasApiCallInProgress: hasApiCallInProgress(state),
	getPayeeById: (id) => getPayeeById(state, id),
	isIFT: isIFT(state),
});

const mapDispatch = {
	getPayeeSrdCheck,
};

export class PayeeList extends Component {
	constructor(props) {
		super(props);
		this.handlePayeeSelect = this.handlePayeeSelect.bind(this);
		this.handleContinue = this.handleContinue.bind(this);
		this.getIsPayeeSelected = this.getIsPayeeSelected.bind(this);
		this.handleNextStep = this.handleNextStep.bind(this);
		this.handleCOPNameNotCheckedModalCancel = this.handleCOPNameNotCheckedModalCancel.bind(this);

		this.state = {
			selectedPayees: [],
			selectedInternalAccountLCV: [],
			showNameNotCheckedModal: false,
		};
	}

	getIsPayeeSelected(payeeId) {
		const { selectedPayees } = this.state;
		const { canAddMultiplePayments, editPaymentPayee } = this.props;
		if (canAddMultiplePayments) return selectedPayees.includes(payeeId);
		return editPaymentPayee === payeeId;
	}

	handlePayeeSelect({ payee, isInternalAccount }) {
		const {
			isLargeCustomerView,
			setLCVAccountsInAccountList,
			canAddMultiplePayments,
			onContinue,
			setPayees,
			removePayee,
			getPayeeById,
		} = this.props;
		const { selectedPayees, selectedInternalAccountLCV } = this.state;
		const selectedAccount = isInternalAccount && payee;

		if (!canAddMultiplePayments) {
			if (isInternalAccount && isLargeCustomerView) setLCVAccountsInAccountList([selectedAccount]);
			const singlePayee = getPayeeById(payee.id);
			if (get(singlePayee, 'confirmation_of_payee.cop_status') === 'inactive') {
				this.setState({ showNameNotCheckedModal: true });
			} else {
				onContinue([payee.id]);
			}
		}
		let newSelectedPayeesId = [];
		let newSelectedInternalAccountLCV = selectedInternalAccountLCV;
		if (selectedPayees.indexOf(payee.id) === -1) {
			if (!isInternalAccount) {
				setPayees([payee]);
			}
			newSelectedPayeesId = [...selectedPayees, payee.id];
			if (isInternalAccount) {
				newSelectedInternalAccountLCV = [...selectedInternalAccountLCV, selectedAccount];
			}
		} else {
			if (!isInternalAccount) {
				removePayee(payee.id);
			}
			newSelectedPayeesId = selectedPayees.filter((id) => id !== payee.id);
			newSelectedInternalAccountLCV = selectedInternalAccountLCV.filter((account) => account.id !== payee.id);
		}
		this.setState({
			selectedPayees: newSelectedPayeesId,
			selectedInternalAccountLCV: newSelectedInternalAccountLCV,
		});
	}

	handleContinue() {
		const { selectedPayees } = this.state;
		const { getPayeeSrdCheck, isIFT } = this.props;
		const isSinglePayee = selectedPayees.length === 1;

		if (isSinglePayee && !isIFT) {
			const singlePayee = this.props.getPayeeById(selectedPayees[0]);

			if (get(singlePayee, 'confirmation_of_payee.cop_status') === 'inactive') {
				this.setState({ showNameNotCheckedModal: true });
			} else {
				this.handleNextStep();
			}
			const { to_account: { account_number, sort_code } = {} } = singlePayee;

			const sortCodeAccountNumber = sort_code && account_number && sort_code.concat(account_number);

			getPayeeSrdCheck(sortCodeAccountNumber);
		} else {
			this.handleNextStep();
		}
	}

	handleNextStep() {
		const { selectedInternalAccountLCV, selectedPayees } = this.state;
		const { setLCVAccountsInAccountList, isLargeCustomerView, onContinue } = this.props;
		if (isLargeCustomerView) setLCVAccountsInAccountList(selectedInternalAccountLCV);
		onContinue(selectedPayees);
	}

	handleCOPNameNotCheckedModalCancel() {
		this.setState({
			selectedPayees: [],
			showNameNotCheckedModal: false,
		});
	}

	render() {
		const {
			showAddPayeeButton,
			canAddMultiplePayments,
			payeeSingularCountMessage,
			paymentsLimitMessage,
			isAddPayeeEnable,
			currentPaymentCount,
			paymentLimit,
			handleCancelPayeeSelect,
			handleDisplayFullwidth,
			paymentMode,
			hasApiCallInProgress,
		} = this.props;

		const { selectedPayees, showNameNotCheckedModal } = this.state;
		const paymentTotal = currentPaymentCount + selectedPayees.length;

		let payeeCountMessage = selectedPayees.length === 1 ? payeeSingularCountMessage : '';
		const showAddPayee = isAddPayeeEnable && showAddPayeeButton && selectedPayees.length === 0;
		payeeCountMessage = paymentTotal >= paymentLimit ? paymentsLimitMessage : payeeCountMessage;

		return showNameNotCheckedModal ? (
			<NameNotCheckedModal
				handleContinue={this.handleNextStep}
				handleCancel={this.handleCOPNameNotCheckedModalCancel}
			/>
		) : (
			<div>
				<PickerLayout
					handleDisplayFullwidth={handleDisplayFullwidth}
					handlePayeeSelect={(account) => this.handlePayeeSelect(account)}
					handleGoBack={handleCancelPayeeSelect}
					selectedPayees={selectedPayees}
					showAddPayee={showAddPayee}
					getIsPayeeSelected={this.getIsPayeeSelected}
					paymentMode={paymentMode}
					isPaymentFlow
				/>
				{canAddMultiplePayments && (
					<PayeeFooterBar
						payeeCountMessage={payeeCountMessage}
						payeeCount={selectedPayees.length}
						payeeSingularCountLabel="Payee selected"
						payeePluralCountLabel="Payees selected"
						onContinue={this.handleContinue}
						disableContinue={hasApiCallInProgress || paymentTotal > paymentLimit}
					/>
				)}
			</div>
		);
	}
}

PayeeList.defaultProps = {
	currentPaymentCount: 0,
	setLCVAccountsInAccountList: () => {},
};

PayeeList.propTypes = {
	payeeSingularCountMessage: PropTypes.string.isRequired,
	paymentsLimitMessage: PropTypes.string.isRequired,
	showAddPayeeButton: PropTypes.bool.isRequired,
	onContinue: PropTypes.func.isRequired,
	isAddPayeeEnable: PropTypes.bool.isRequired,
	canAddMultiplePayments: PropTypes.bool.isRequired,
	currentPaymentCount: PropTypes.number,
	paymentLimit: PropTypes.number.isRequired,
	editPaymentPayee: PropTypes.string,
	isLargeCustomerView: PropTypes.bool.isRequired,
	setLCVAccountsInAccountList: PropTypes.func,
	setPayees: PropTypes.func.isRequired,
	removePayee: PropTypes.func.isRequired,
	handleCancelPayeeSelect: PropTypes.func.isRequired,
	handleDisplayFullwidth: PropTypes.func,
	paymentMode: PropTypes.string,
	hasApiCallInProgress: PropTypes.bool.isRequired,
	getPayeeById: PropTypes.func.isRequired,
};
export const ConnectWrapper = connect(mapState, mapDispatch)(PayeeList);
export default ConnectWrapper;
